import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  TextField,
  IconButton,
  Button,
  MenuItem,
} from "@mui/material";
import { FaEdit } from "react-icons/fa";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast, { Toaster } from "react-hot-toast";
import PhoneInput from "react-phone-input-2";
import 'react-phone-input-2/lib/style.css';

// Fetch profile function
const fetchProfile = async () => {
  const token = localStorage.getItem("tokenInv");
  const response = await axios.get(`${process.env.REACT_APP_URL}/api/profile`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "ngrok-skip-browser-warning": "true",
    },
  });
  return response.data.data;
};

const ProfilePage = ({ showSidebarToggle }) => {
  const queryClient = useQueryClient();
  const {
    data: profile,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["userProfile"],
    queryFn: fetchProfile,
    retry: false,
  });

  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});

useEffect(() => {
  if (profile) {
    const formatted = {
      name: profile.name || "",
      email: profile.email || "",
      phone: profile.phone_number || "", // updated key
      gender: profile.gender || "",
      age: profile.age || "",
      address: profile.address || "",
    };
    setFormData(formatted);
    setOriginalData(formatted);
  }
}, [profile]);


  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleCancel = () => {
    setFormData(originalData);
    setEditMode(false);
  };

const handleSave = async () => {
  try {
    const token = localStorage.getItem("tokenInv");

    const payload = {
      name: formData.name,
      email: formData.email,
      phone_number: formData.phone,
      gender: formData.gender, // assuming API expects lowercase
      age: Number(formData.age),
      address: formData.address,
    };

    await axios.put(`${process.env.REACT_APP_URL}/user/${profile.id}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    toast.success("Profile updated successfully!");
    queryClient.invalidateQueries(["userProfile"]);
    setEditMode(false);
  } catch (err) {
    toast.error(
      err?.response?.data?.error || "Failed to update profile. Please try again."
    );
  }
};


  const fields = [
    { label: "Name", key: "name" },
    { label: "Email", key: "email" },
    { label: "Phone Number", key: "phone" },
    { label: "Gender", key: "gender" },
    { label: "Age", key: "age" },
    { label: "Address", key: "address" },
  ];

  if (isLoading) {
    return (
      <Box p={4} textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p={4} textAlign="center">
        <Typography color="error">
          {error?.response?.data?.message || error?.message || "An unknown error occurred"}
        </Typography>
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="body1">No profile data available.</Typography>
      </Box>
    );
  }


  return (
    <Box p={4}>
      <Toaster position="top-right" reverseOrder={false}  />
      <Typography variant="h4" gutterBottom>
        My Profile
      </Typography>

      <Paper elevation={4} sx={{ position: "relative", p: 3, borderRadius: 3 }}>
        {/* Edit Button */}
        {!editMode && (
          <IconButton
            onClick={() => setEditMode(true)}
            sx={{ position: "absolute", top: 16, right: 16 }}
            size="small"
          >
            <FaEdit size={18} />
          </IconButton>
        )}

        {/* Profile Fields */}
        <Grid container spacing={3}>
          {fields.map((field) => (
            <Grid item xs={12} sm={6} key={field.key}>
              <Box sx={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {field.label}
                </Typography>

                {editMode ? (
                  field.key === "phone" ? (
                    <PhoneInput
                      country={"in"}
                      value={formData.phone}
                      onChange={(value) => handleChange("phone", value)}
                      inputStyle={{ width: "100%" }}
                    />
                  ) : field.key === "gender" ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={formData.gender}
                      onChange={(e) => handleChange("gender", e.target.value)}
                    >
                      <MenuItem value="">Select Gender</MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </TextField>
                  ) : field.key === "age" ? (
                    <TextField
                      type="number"
                      fullWidth
                      size="small"
                      value={formData.age}
                      onChange={(e) => handleChange("age", e.target.value)}
                    />
                  ) : (
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={formData[field.key]}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      disabled={field.key === "email"}
                    />
                  )
                ) : (
                 <Typography
  variant="body1"
  fontWeight="500"
  sx={{
    textTransform: field.key === "email" ? "none" : "capitalize",
    wordBreak: "break-word",
  }}
>
  {formData[field.key] || "N/A"}
</Typography>

                )}
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Action Buttons */}
        {editMode && (
          <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" color="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave}>
              Save
            </Button>
          </Box>
        )}
      </Paper>
      {!showSidebarToggle && (
      <Typography
        variant="body1"
        color="error"
        sx={{ mb: 2, fontWeight: "bold", textAlign:"center", mt:3 }}
      >
        You don't have access to any other tab.
      </Typography>
    )}
    </Box>
  );
};

export default ProfilePage;
